/**
 * LE GUIDE — France Actes, écran par écran.
 *
 * The original site carried a Guideline page: what every screen is for, what
 * every button does, what every label means. It came over because the section
 * is not obvious — a dossier, a demande, a DPC and an autorité are four
 * different things that all look like "a row in a table" — and because the
 * team learned the old site from it.
 *
 * It is NOT a copy. The pages here are the portal's pages, the notifications
 * are the portal's bell, and the things the merge deliberately left behind
 * (the Inbox page, the broadcasts, the e-mail administration) are not
 * described as if they existed. A guide that describes a screen nobody can
 * open is worse than no guide.
 */
import { useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon, type IconName } from '@/components/kbIcons';
import type { ActesReference } from '@/integrations/actesApi';
import { countryOfRef, portugalWords } from '@/lib/actesCountry';

type L = { fr: string; en: string };
type Item = { icon: IconName; name: L; desc: L };

const PAGES: { key: string; icon: IconName; label: L }[] = [
  { key: 'overview', icon: 'logbook', label: { fr: 'Vue d’ensemble', en: 'Overview' } },
  { key: 'dossiers', icon: 'documents', label: { fr: 'Dossiers', en: 'Dossiers' } },
  { key: 'demandes', icon: 'tasks', label: { fr: 'Demandes', en: 'Requests' } },
  { key: 'reception', icon: 'ai', label: { fr: 'Réception (IA)', en: 'Intake (AI)' } },
  { key: 'dpc', icon: 'hourglass', label: { fr: 'Compléments', en: 'Complements' } },
  { key: 'autorites', icon: 'building', label: { fr: 'Autorités', en: 'Authorities' } },
  { key: 'ticket', icon: 'case', label: { fr: 'Depuis un dossier client', en: 'From a case' } },
];

export function GuidePanel({ lang, reference }: { lang: Lang; reference: ActesReference }) {
  const en = lang !== 'fr';
  const T = (s: L) => (en ? s.en : s.fr);
  const [page, setPage] = useState('overview');
  const statuses = reference.acteStatuses ?? [];

  /* Portugal has its own guide: the same section, a lighter flow. */
  if (countryOfRef(reference) === 'PT') return <PortugalGuide reference={reference} />;

  return (
    <section className="actes-panel v7 gd">
      <div className="pillrow">
        {PAGES.map((p) => (
          <button key={p.key} className="fp" aria-pressed={page === p.key} onClick={() => setPage(p.key)}>
            <KBIcon name={p.icon} size={12} /> {T(p.label)}
          </button>
        ))}
      </div>

      {page === 'overview' && (
        <>
          <Intro>{T({
            fr: 'Comment tout se tient : un dossier réunit les demandes d’acte d’un client ; chaque demande a un état, un responsable et des dates ; quand le document arrive, il est rattaché à sa demande et les dates se remplissent.',
            en: 'How it all holds together: a dossier gathers a client’s civil-record requests; each request has a status, an owner and dates; when the document arrives it is attached to its request and the dates fill in.',
          })}</Intro>
          <Flow steps={[
            { icon: 'documents', label: T({ fr: 'Dossier', en: 'Dossier' }) },
            { icon: 'tasks', label: T({ fr: 'Demandes d’acte', en: 'Civil-record requests' }) },
            { icon: 'ai', label: T({ fr: 'Réception (IA)', en: 'Intake (AI)' }) },
            { icon: 'check', label: T({ fr: 'État + dates', en: 'Status + dates' }) },
            { icon: 'bell', label: T({ fr: 'Notification', en: 'Notification' }) },
          ]} />
          <Sec icon="grid" title={T({ fr: 'Les pages', en: 'The pages' })}>
            <Grid items={[
              { icon: 'grid', name: { fr: 'Tableau de bord', en: 'Dashboard' }, desc: { fr: 'Vos chiffres et ceux de l’équipe : ce qui est en retard, ce qui expire, ce qui vient d’arriver.', en: 'Your figures and the team’s: what is late, what expires, what has just arrived.' } },
              { icon: 'documents', name: { fr: 'Dossiers', en: 'Dossiers' }, desc: { fr: 'Un dossier = un client / une procédure, qui regroupe toutes ses demandes d’acte.', en: 'A dossier = one client / procedure, gathering all its civil-record requests.' } },
              { icon: 'tasks', name: { fr: 'Demandes', en: 'Requests' }, desc: { fr: 'La table des demandes : les vôtres, toutes, celles sans dossier, et les derniers scans.', en: 'The requests table: yours, everyone’s, the ones with no dossier, and the latest scans.' } },
              { icon: 'hourglass', name: { fr: 'Compléments (DPC)', en: 'Complements (DPC)' }, desc: { fr: 'Le suivi des pièces complémentaires demandées par le tribunal (CNF).', en: 'Tracking of the complementary documents the court asks for (CNF).' } },
              { icon: 'building', name: { fr: 'Autorités', en: 'Authorities' }, desc: { fr: 'Le carnet d’adresses des mairies, SCEC et archives qui délivrent les actes.', en: 'The address book of town halls, SCEC and archives that issue the records.' } },
              { icon: 'ai', name: { fr: 'Réception (IA)', en: 'Intake (AI)' }, desc: { fr: 'Le courrier reçu, scanné et lu par l’IA, qui le rattache à la bonne demande.', en: 'The incoming mail, scanned and read by the AI, which files it on the right request.' } },
              { icon: 'trash', name: { fr: 'Corbeille', en: 'Trash' }, desc: { fr: 'Ce qui a été supprimé, et par qui — récupérable. Réservée aux super admins.', en: 'What was deleted, and by whom — recoverable. Super admins only.' } },
              { icon: 'logbook', name: { fr: 'Guide', en: 'Guide' }, desc: { fr: 'Cette page.', en: 'This page.' } },
            ]} T={T} />
          </Sec>
          <Sec icon="ai" title={T({ fr: 'Bon à savoir', en: 'Good to know' })}>
            <Grid items={[
              { icon: 'bell', name: { fr: 'Les notifications', en: 'Notifications' }, desc: { fr: 'Elles arrivent dans la cloche du portail, avec celles des dossiers clients — pas dans une boîte séparée. Chaque lien ouvre la chose dont il parle.', en: 'They arrive in the portal’s bell, together with the case ones — not in a separate mailbox. Every link opens the thing it names.' } },
              { icon: 'reset', name: { fr: 'Annuler', en: 'Undo' }, desc: { fr: 'Une suppression ou un déplacement affiche « Annuler » quelques secondes en bas de l’écran.', en: 'A deletion or a move offers “Undo” for a few seconds at the bottom of the screen.' } },
              { icon: 'grip', name: { fr: 'Lignes cliquables', en: 'Clickable rows' }, desc: { fr: 'Dans la table des demandes, cliquez n’importe où sur une ligne pour l’ouvrir.', en: 'In the requests table, click anywhere on a row to open it.' } },
              { icon: 'lang', name: { fr: 'FR / EN', en: 'FR / EN' }, desc: { fr: 'La langue vient du portail (en haut à droite) : la section suit.', en: 'The language comes from the portal (top right): the section follows.' } },
            ]} T={T} />
          </Sec>
        </>
      )}

      {page === 'dossiers' && (
        <>
          <Intro>{T({
            fr: 'Ouvrez un dossier et tout s’y gère : ses demandes, son avancement, ses documents, ses notes et le dossier client auquel il est rattaché.',
            en: 'Open a dossier and everything is managed there: its requests, its progress, its documents, its notes and the case it is attached to.',
          })}</Intro>
          <Grid T={T} items={[
            { icon: 'tasks', name: { fr: 'Ses demandes', en: 'Its requests' }, desc: { fr: 'La liste des actes demandés et l’avancement (% reçus). Une ligne s’ouvre d’un clic.', en: 'The records asked for and the progress (% received). A row opens in one click.' } },
            { icon: 'case', name: { fr: 'Le dossier client', en: 'The case' }, desc: { fr: 'Le ticket HubSpot lié — cherché par nom, par id HubSpot ou par id Parallel, et modifiable après coup depuis « Éditer ».', en: 'The linked HubSpot ticket — searched by name, HubSpot id or Parallel id, and changeable afterwards from “Edit”.' } },
            { icon: 'users', name: { fr: 'Avocat principal & équipe', en: 'Lead lawyer & team' }, desc: { fr: 'L’avocat principal se choisit parmi les avocats ; les autres membres sont les collaborateurs du dossier.', en: 'The lead is picked among the lawyers; the other members are the dossier’s collaborators.' } },
            { icon: 'ext', name: { fr: 'Google Drive', en: 'Google Drive' }, desc: { fr: 'Le dossier Drive lié s’ouvre d’un clic ; le lien se colle depuis « Éditer ».', en: 'The linked Drive folder opens in one click; the link is pasted from “Edit”.' } },
            { icon: 'download', name: { fr: 'Tout télécharger', en: 'Download everything' }, desc: { fr: 'Tous les documents reçus du dossier, en un seul zip.', en: 'Every received document of the dossier, in one zip.' } },
            { icon: 'mail', name: { fr: 'Suivi de la Poste', en: 'Postal tracking' }, desc: { fr: 'Les courriers et accusés de réception classés sur ce dossier.', en: 'The letters and acknowledgements filed on this dossier.' } },
            { icon: 'clock', name: { fr: 'Historique', en: 'History' }, desc: { fr: 'Tout ce qui a bougé : états, notes, ajouts — qui, quoi, quand.', en: 'Everything that moved: statuses, notes, additions — who, what, when.' } },
            { icon: 'ai', name: { fr: 'Import Parallel', en: 'Parallel import' }, desc: { fr: 'Un PDF de réponses Parallel crée le dossier complet avec ses demandes. Depuis la liste des dossiers.', en: 'A Parallel replies PDF creates the whole dossier with its requests. From the dossier list.' } },
            { icon: 'search', name: { fr: 'Doublons', en: 'Duplicates' }, desc: { fr: 'Le scan PROPOSE des doublons de demandes ; rien n’est coché d’avance, vous décidez.', en: 'The scan PROPOSES duplicate requests; nothing is ticked in advance, you decide.' } },
            { icon: 'trash', name: { fr: 'Supprimer', en: 'Delete' }, desc: { fr: 'Va à la Corbeille, récupérable, et « Annuler » reste quelques secondes à l’écran. Réservé aux super admins.', en: 'Goes to the Trash, recoverable, and “Undo” stays on screen for a few seconds. Super admins only.' } },
          ]} />
        </>
      )}

      {page === 'demandes' && (
        <>
          <Intro>{T({
            fr: 'La table s’ouvre sur VOS demandes — les autres vues sont demandées exprès, parce qu’il y en a plusieurs milliers.',
            en: 'The table opens on YOUR requests — the other scopes are asked for on purpose, because there are several thousand of them.',
          })}</Intro>
          <Sec icon="tasks" title={T({ fr: 'Les vues', en: 'The scopes' })}>
            <Grid T={T} items={[
              { icon: 'user', name: { fr: 'Les miennes', en: 'Mine' }, desc: { fr: 'Les demandes dont vous êtes responsable. C’est la vue de départ.', en: 'The requests you are responsible for. The starting view.' } },
              { icon: 'users', name: { fr: 'Toutes', en: 'All' }, desc: { fr: 'Toute l’équipe.', en: 'The whole team.' } },
              { icon: 'x', name: { fr: 'Sans dossier', en: 'No dossier' }, desc: { fr: 'Créées ainsi, ou reçues par la réception sans avoir été identifiées. Assignez-leur un dossier et elles le rejoignent.', en: 'Created that way, or received by the intake without being identified. Give them a dossier and they join it.' } },
              { icon: 'ai', name: { fr: 'Derniers scans', en: 'Latest scans' }, desc: { fr: 'Ce qui vient d’arriver : le document, sa demande, qui l’a déposé.', en: 'What has just arrived: the document, its request, who filed it.' } },
            ]} />
          </Sec>
          <Sec icon="sliders" title={T({ fr: 'La ligne, colonne par colonne', en: 'The row, column by column' })}>
            <Grid T={T} items={[
              { icon: 'eye', name: { fr: 'Document', en: 'Document' }, desc: { fr: 'L’œil au début de la ligne ouvre le document reçu ; le chiffre dit combien il y en a. Plusieurs → une liste à choisir.', en: 'The eye at the start of the row opens the received document; the number says how many. Several → a list to pick from.' } },
              { icon: 'user', name: { fr: 'Responsable', en: 'Owner' }, desc: { fr: 'L’avatar EST le contrôle : cliquez-le pour changer de responsable. Pas de nom en double à côté.', en: 'The avatar IS the control: click it to change owner. No duplicated name beside it.' } },
              { icon: 'check', name: { fr: 'État', en: 'Status' }, desc: { fr: 'L’étiquette de couleur, modifiable sur place.', en: 'The coloured label, changed in place.' } },
              { icon: 'calendar', name: { fr: 'Expiration', en: 'Expires' }, desc: { fr: 'Émission + 3 mois, calculée. « ? » tant que l’acte n’est pas reçu.', en: 'Issue + 3 months, computed. “?” until the record is received.' } },
              { icon: 'key', name: { fr: 'Référence', en: 'Reference' }, desc: { fr: 'La référence de la demande auprès de l’administration.', en: 'The request’s reference with the administration.' } },
              { icon: 'chat', name: { fr: 'Notes', en: 'Notes' }, desc: { fr: 'Le chiffre = combien. On les lit sans ouvrir la fiche, et on peut les supprimer.', en: 'The number = how many. Read without opening the row, and removable.' } },
              { icon: 'edit', name: { fr: 'Crayon', en: 'Pencil' }, desc: { fr: 'Modifier la demande, y compris depuis le bloc affiché sur le dossier client.', en: 'Edit the request, including from the block shown on the case page.' } },
            ]} />
          </Sec>
          <Sec icon="check" title={T({ fr: 'Les états', en: 'The statuses' })}>
            <div className="gd-chips">
              {statuses.map((s) => (
                <span key={s.id} className="res-chip static" style={s.color ? { borderColor: s.color, color: s.color } : undefined}>
                  {en ? (s.label_en || s.label_fr) : s.label_fr}
                </span>
              ))}
              {statuses.length === 0 && <p className="muted-note">{T({ fr: 'Les états se gèrent depuis Administration → France Actes.', en: 'The statuses are managed from Administration → France Actes.' })}</p>}
            </div>
          </Sec>
          <Sec icon="plus" title={T({ fr: 'Bien remplir une demande', en: 'Filling a request well' })}>
            <Steps items={[
              { fr: 'Le dossier : cherchez-le, ou laissez vide — la demande sera « sans dossier » et rattachable plus tard.', en: 'The dossier: search for it, or leave it empty — the request will be “no dossier” and attachable later.' },
              { fr: 'La personne concernée, le type d’acte et l’autorité. Un type ou une autorité qui n’existe pas encore se crée ici même.', en: 'The person concerned, the record type and the authority. A type or an authority that does not exist yet is created right here.' },
              { fr: 'La date de demande et « Demandé via » : courrier ou e-mail.', en: 'The request date and “Requested via”: post or e-mail.' },
              { fr: 'L’état de départ est « À demander à l’adm » — changez-le si la demande est déjà partie.', en: 'The starting status is “To request from the adm.” — change it if the request has already gone out.' },
              { fr: 'À la réception, l’émission est lue automatiquement : l’expiration (+3 mois) et la date de réception se remplissent seules.', en: 'On intake the issue date is read automatically: the expiry (+3 months) and the reception date fill in on their own.' },
            ]} T={T} />
          </Sec>
          <Sec icon="reset" title={T({ fr: 'Redemander le même acte', en: 'Re-requesting the same record' })}>
            <Note>
              {T({
                fr: 'Le précédent a expiré ou n’a rien donné : mettez à jour la date de demande et « Demandé via », repassez l’état sur « À redemander », et à la nouvelle réception l’expiration se recalcule. Le nouveau document S’AJOUTE à l’ancien — rien n’est jamais remplacé.',
                en: 'The previous one expired or came to nothing: update the request date and “Requested via”, set the status back to “To re-request”, and on the new intake the expiry recomputes. The new document is ADDED next to the old one — nothing is ever replaced.',
              })}
            </Note>
          </Sec>
        </>
      )}

      {page === 'reception' && (
        <>
          <Intro>{T({
            fr: 'Le courrier arrive au cabinet, il est scanné ici, et l’IA le range. Tout le monde peut déposer ; ce qui est déposé garde le nom de qui l’a fait.',
            en: 'The mail arrives at the office, it is scanned here, and the AI files it. Anyone can file; what is filed keeps the name of who did it.',
          })}</Intro>
          <Steps T={T} items={[
            { fr: 'Vous déposez le scan (photo ou PDF). L’IA lit la personne, le type d’acte, l’autorité, la date d’émission et l’origine (français / étranger).', en: 'You file the scan (photo or PDF). The AI reads the person, the record type, the authority, the issue date and the origin (French / foreign).' },
            { fr: 'Elle propose la demande qui correspond. Vous voyez ce qu’elle a lu et ce qu’elle va écrire AVANT de valider — rien n’est appliqué dans votre dos.', en: 'It proposes the matching request. You see what it read and what it will write BEFORE confirming — nothing is applied behind your back.' },
            { fr: 'À la validation, le fichier s’AJOUTE à la demande, l’état passe à « Reçu physiquement » et les dates se remplissent.', en: 'On confirming, the file is ADDED to the request, the status becomes “Received” and the dates fill in.' },
            { fr: 'Si elle n’identifie pas la demande, le document part dans « Sans dossier » et l’équipe est prévenue : vérifiez s’il vous concerne.', en: 'If it cannot identify the request, the document lands in “No dossier” and the team is told: check whether it concerns you.' },
            { fr: 'Tout dépôt reste visible dans « Derniers scans », avec le scan.', en: 'Every filing stays visible under “Latest scans”, with the scan.' },
          ]} />
          <Note>
            {T({
              fr: 'L’historique des dépôts se lit par jour : une ligne par dépôt, chaque journée se replie et se déplie.',
              en: 'The filing history reads by day: one line per filing, each day folds and unfolds.',
            })}
          </Note>
        </>
      )}

      {page === 'dpc' && (
        <>
          <Intro>{T({
            fr: 'Compléments = les pièces que le tribunal réclame EN PLUS des actes. Une date butoir manquée fait refuser le dossier, d’où la page à part.',
            en: 'Complements = the documents the court asks for ON TOP of the records. A missed deadline gets the file refused, hence its own page.',
          })}</Intro>
          <Grid T={T} items={[
            { icon: 'plus', name: { fr: 'Créer', en: 'Create' }, desc: { fr: 'Client, dossier, n° de demande, date de réception de l’AR, échéance, responsable, état et notes.', en: 'Client, dossier, request number, AR reception date, deadline, owner, status and notes.' } },
            { icon: 'documents', name: { fr: 'Joindre', en: 'Attach' }, desc: { fr: 'PDF, JPG ou PNG. Visible ici et dans le dossier.', en: 'PDF, JPG or PNG. Visible here and in the dossier.' } },
            { icon: 'bell', name: { fr: 'L’alerte', en: 'The alert' }, desc: { fr: 'Trois jours avant l’échéance, tout le monde sur le dossier est prévenu — une seule fois, pas tous les matins.', en: 'Three days before the deadline everyone on the dossier is told — once, not every morning.' } },
            { icon: 'documents', name: { fr: 'Dans le dossier', en: 'In the dossier' }, desc: { fr: 'Les compléments d’un client apparaissent aussi à l’intérieur de son dossier, modifiables depuis là.', en: 'A client’s complements also appear inside their dossier, editable from there.' } },
          ]} />
        </>
      )}

      {page === 'autorites' && (
        <>
          <Intro>{T({
            fr: 'Autorités = le carnet d’adresses des administrations qui délivrent les actes. Chaque section se replie.',
            en: 'Authorities = the address book of the administrations that issue the records. Each section folds.',
          })}</Intro>
          <Grid T={T} items={[
            { icon: 'building', name: { fr: 'Le répertoire', en: 'The directory' }, desc: { fr: 'Mairies, SCEC, archives — par catégorie, avec e-mail, téléphone, adresse et département.', en: 'Town halls, SCEC, archives — by category, with e-mail, phone, address and department.' } },
            { icon: 'plus', name: { fr: 'Créer à la volée', en: 'Create on the fly' }, desc: { fr: 'Depuis une demande, le champ « Autorité » cherche ou crée : pas besoin de venir ici d’abord.', en: 'From a request, the “Authority” field searches or creates: no need to come here first.' } },
            { icon: 'ai', name: { fr: 'La chercher pour vous', en: 'Look it up' }, desc: { fr: 'L’IA cherche les coordonnées d’une mairie sur le web et les propose — à vérifier avant d’enregistrer.', en: 'The AI looks a town hall’s details up on the web and proposes them — check before saving.' } },
            { icon: 'search', name: { fr: 'Ses demandes', en: 'Its requests' }, desc: { fr: 'Depuis Demandes, chercher par autorité liste tout ce qui a été demandé à celle-ci.', en: 'From Requests, searching by authority lists everything asked of it.' } },
          ]} />
        </>
      )}

      {page === 'ticket' && (
        <>
          <Intro>{T({
            fr: 'La section vit aussi DANS le dossier client : un bloc « Actes » sur la fiche du ticket, pour les dossiers de nationalité et de CNF.',
            en: 'The section also lives INSIDE the case: an “Actes” block on the ticket page, for nationality and CNF cases.',
          })}</Intro>
          <Grid T={T} items={[
            { icon: 'documents', name: { fr: 'Le dossier lié', en: 'The linked dossier' }, desc: { fr: 'Ses demandes, leur état, leur expiration, la personne concernée et l’œil pour voir le document — sans quitter le ticket.', en: 'Its requests, their status, their expiry, the person concerned and the eye to see the document — without leaving the ticket.' } },
            { icon: 'search', name: { fr: 'S’il n’y en a pas', en: 'If there is none' }, desc: { fr: 'Cherchez d’abord : la recherche propose les dossiers proches avec un degré de confiance. Créer un dossier n’est proposé que s’il n’y en a vraiment pas.', en: 'Search first: it proposes the near dossiers with a confidence. Creating one is only offered when there really is none.' } },
            { icon: 'plus', name: { fr: 'Créer une demande', en: 'Create a request' }, desc: { fr: 'Depuis le ticket, avec le dossier déjà rempli.', en: 'From the ticket, with the dossier already filled in.' } },
            { icon: 'reset', name: { fr: 'Les deux côtés bougent ensemble', en: 'Both sides move together' }, desc: { fr: 'Ce que vous changez ici se voit dans Actes, et l’inverse : c’est la même donnée, pas une copie.', en: 'What you change here shows in Actes, and the other way round: it is the same data, not a copy.' } },
          ]} />
        </>
      )}
    </section>
  );
}

/**
 * THE PORTUGAL GUIDE — Actes for Portuguese nationality cases, in English.
 *
 * Lighter than France on purpose: no DPC, no Poste log, no authorities
 * register. A dossier is born on its nationality case and lives there; its
 * requests say in words which document is needed for whom; the intake files
 * what arrives. Same building blocks as France's guide, its own words.
 */
const PT_PAGES = [
  { key: 'overview', icon: 'logbook', label: 'Overview' },
  { key: 'create', icon: 'case', label: 'Create a dossier' },
  { key: 'requests', icon: 'tasks', label: 'Requests' },
  { key: 'intake', icon: 'ai', label: 'Intake (AI)' },
  { key: 'statuses', icon: 'check', label: 'Statuses' },
] as const satisfies readonly { key: string; icon: IconName; label: string }[];
const same = (s: string): L => ({ fr: s, en: s });
const item = (icon: IconName, name: string, desc: string): Item => ({ icon, name: same(name), desc: same(desc) });

function PortugalGuide({ reference }: { reference: ActesReference }) {
  const T = (s: L) => portugalWords(s.en);
  const [page, setPage] = useState<(typeof PT_PAGES)[number]['key']>('overview');
  const statuses = reference.acteStatuses ?? [];
  return (
    <section className="actes-panel v7 gd">
      <div className="pillrow">
        {PT_PAGES.map((p) => (
          <button key={p.key} className="fp" aria-pressed={page === p.key} onClick={() => setPage(p.key)}>
            <KBIcon name={p.icon} size={12} /> {p.label}
          </button>
        ))}
      </div>

      {page === 'overview' && (
        <>
          <Intro>Each Portuguese nationality case has one dossier. The dossier lists, person by person, the documents the application needs; each document is a request with a status, an owner and dates. When a document arrives, the intake reads it and files it on its request, and the dossier’s progress moves.</Intro>
          <Flow steps={[
            { icon: 'case', label: 'Nationality case' },
            { icon: 'documents', label: 'Dossier' },
            { icon: 'tasks', label: 'Requests' },
            { icon: 'ai', label: 'Intake (AI)' },
            { icon: 'check', label: 'Received' },
          ]} />
          <Sec icon="grid" title="The pages">
            <Grid T={T} items={[
              item('grid', 'Dashboard', 'Your dossiers, what is still pending on your requests, what expires soon, what was received, and the latest documents filed.'),
              item('documents', 'Dossiers', 'Portugal’s nationality dossiers. A dossier is created from its case (see «Create a dossier»), not from here.'),
              item('tasks', 'Requests', 'Every document asked for: yours, or everyone’s. Each one belongs to a dossier.'),
              item('ai', 'Intake (AI)', 'Drop the scans: the AI reads each one and proposes the request it belongs to. Nothing is filed until you validate.'),
              item('logbook', 'Guide', 'This page.'),
              item('trash', 'Trash', 'What was deleted, and by whom — and the way back. Open to the Portugal team.'),
            ]} />
          </Sec>
          <Sec icon="ai" title="Good to know">
            <Grid T={T} items={[
              item('lang', 'Always in English', 'Portugal’s Actes speak English whatever the portal’s language.'),
              item('case', 'Portugal only', 'You see Portugal’s dossiers only, and France sees only its own. Super admins switch with the country selector at the top of the portal.'),
              item('bell', 'Notifications', 'They arrive in the portal’s bell: a dossier you are put on, a document received on your dossier.'),
              item('x', 'Not in Portugal', 'No complementary-documents (DPC) tracking, no post log and no authorities register — those are France’s.'),
            ]} />
          </Sec>
        </>
      )}

      {page === 'create' && (
        <>
          <Intro>A dossier is born on its nationality case, so it always knows its case — and that is what makes it Portugal’s.</Intro>
          <Steps T={T} items={[
            same('Open the case (pipeline «Portuguese Nationality» or «PT - Nationality») and find the «Civil records (Actes)» block, under Parallel.'),
            same('Press «Create a new one». The dossier is named after the client, with the case’s owner as lead and its paralegals as team; the HubSpot, Parallel and Drive links come from the case.'),
            same('Press «Fill from Parallel». The client’s Parallel form is read and the documents each applicant needs are proposed — birth certificates, criminal records, the Portuguese ancestor’s or spouse’s certificates… Untick what does not apply, correct the wording, then add.'),
            same('Open the dossier («Open in Actes»). Its header shows the case’s IRN consultation key when HubSpot has one.'),
          ]} />
          <Note>A proposal is a starting point, not a checklist: the form only knows what the client wrote. Anything it does not name is added by hand with «New request». A Portuguese dossier cannot be detached from its case — one made by mistake goes to the trash.</Note>
        </>
      )}

      {page === 'requests' && (
        <>
          <Intro>One request = one document for one person.</Intro>
          <Sec icon="tasks" title="What a request holds">
            <Grid T={T} items={[
              item('user', 'Person concerned', 'Who the document is about — the applicant, a parent, the Portuguese grandparent, the spouse.'),
              item('documents', 'Document requested', 'In words: e.g. «Birth certificate (full copy) — Conservatória do Registo Civil», «Criminal record certificate — Brazil».'),
              item('layers', 'Type (optional)', 'A type from the shared list, when one fits exactly.'),
              item('check', 'Status and owner', 'Where it stands, and who is chasing it. The request date fills in by itself when it is sent.'),
              item('calendar', 'Dates', 'Requested, issued, received, expires. The issue date is read from the document; the expiry is worked out from it.'),
              item('globe', 'Origin', 'Portuguese (Conservatória, IRN, a Portuguese consulate) or foreign.'),
            ]} />
          </Sec>
          <Note>In Portugal every request lives in a dossier — there is no «no dossier» pile.</Note>
        </>
      )}

      {page === 'intake' && (
        <>
          <Intro>Drop the scans (PDF or image, up to 15 MB each, several at once). Two are read at a time.</Intro>
          <Steps T={T} items={[
            same('Optionally write what each document is; otherwise the AI detects it.'),
            same('The AI reads the person, the document, the issuer, the issue date and the record number or online-certificate code — nothing else.'),
            same('It looks for the request by name among Portugal’s dossiers and proposes one, with the other candidates. No match: create the request on the spot, in its dossier.'),
            same('You validate. Only then is the document stored, the request marked «received», and the dates filled in; the dossier’s team is told.'),
          ]} />
          <Note>Pages that do not belong to the same document or person are flagged and must be checked before validating.</Note>
        </>
      )}

      {page === 'statuses' && (
        <>
          <Intro>The statuses are the shared ones, in English. «Received» statuses count towards the dossier’s progress.</Intro>
          <div className="gd-grid">
            {statuses.map((s) => (
              <div key={s.id} className="gd-item">
                <span className="gd-item-ico"><KBIcon name={s.is_received ? 'check' : 'clock'} size={14} /></span>
                <span className="gd-item-t">
                  <b>{s.label_en}</b>
                  <em>{s.is_received ? 'counts as received' : s.is_terminal ? 'ends the line' : 'in progress'}</em>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function Sec({ icon, title, children }: { icon: IconName; title: string; children: React.ReactNode }) {
  return (
    <section className="gd-sec">
      <h3 className="gd-sec-h"><KBIcon name={icon} size={14} /> {title}</h3>
      {children}
    </section>
  );
}
function Intro({ children }: { children: React.ReactNode }) {
  return <p className="gd-intro">{children}</p>;
}
function Note({ children }: { children: React.ReactNode }) {
  return <div className="gd-note">{children}</div>;
}
function Grid({ items, T }: { items: Item[]; T: (s: L) => string }) {
  return (
    <div className="gd-grid">
      {items.map((i) => (
        <div key={i.name.fr} className="gd-item">
          <span className="gd-item-ico"><KBIcon name={i.icon} size={14} /></span>
          <span className="gd-item-t">
            <b>{T(i.name)}</b>
            <em>{T(i.desc)}</em>
          </span>
        </div>
      ))}
    </div>
  );
}
function Steps({ items, T }: { items: L[]; T: (s: L) => string }) {
  return (
    <ol className="gd-steps">
      {items.map((s, i) => (
        <li key={i}><span className="gd-step-n">{i + 1}</span><span>{T(s)}</span></li>
      ))}
    </ol>
  );
}
function Flow({ steps }: { steps: { icon: IconName; label: string }[] }) {
  return (
    <div className="gd-flow">
      {steps.map((s, i) => (
        <span key={s.label} className="gd-flow-i">
          <span className="gd-flow-b"><KBIcon name={s.icon} size={13} /> {s.label}</span>
          {i < steps.length - 1 && <KBIcon name="chevron-right" size={13} />}
        </span>
      ))}
    </div>
  );
}
